/**
 * Thin typed fetch wrapper around this Worker's /api/* proxy (see
 * ../worker/src/index.ts) — every call carries the SSO bearer token
 * explicitly (there's no Service Worker injecting it here, unlike
 * internal/app's api.ts; this app's token lives in sessionStorage via
 * lib/auth.tsx instead).
 */

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function api<T>(
  token: string,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch("/api" + path, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init.headers ?? {}),
      authorization: "Bearer " + token,
    },
  });
  const body = await parseBody(res);
  if (!res.ok) {
    const message =
      body &&
      typeof body === "object" &&
      "error" in body &&
      typeof body.error === "string"
        ? body.error
        : res.statusText;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}
